import { useEffect, useState } from 'react'
import type { PassTier } from '../data/placesIndex'
import { PLACES_COUNT_FULL, PLACES_COUNT_MINI } from '../data/placesIndex'
import {
  fetchHotpayPricing,
  pricingForTier,
  type HotpayPricingResponse,
  type PassTierPricing,
} from '../lib/api/hotpay'
import { formatKwotaPln, parseKwotaNumber } from '../lib/formatKwota'
import {
  FULL_REGULAR_KWOTA,
  isLaunchPromoPricingFallbackActive,
  isPromoKwota,
  launchPromoKwotaForTier,
  launchPromoRegularKwotaForTier,
  MINI_REGULAR_KWOTA,
} from '../lib/promo/launchPromo'

export type HotpayKwotaState = {
  kwota: string | null
  regularKwota: string
  label: string | null
  regularLabel: string
  amount: number | null
  promoActive: boolean
  loading: boolean
  error: string | null
}

export type HotpayPricingState = {
  pricing: HotpayPricingResponse | null
  loading: boolean
  error: string | null
}

function fallbackTierState(tier: PassTier): Omit<HotpayKwotaState, 'loading' | 'error'> | null {
  if (!isLaunchPromoPricingFallbackActive(tier)) return null
  const kwota = launchPromoKwotaForTier(tier)
  const regularKwota = launchPromoRegularKwotaForTier(tier)
  return {
    kwota,
    regularKwota,
    label: formatKwotaPln(kwota),
    regularLabel: formatKwotaPln(regularKwota),
    amount: parseKwotaNumber(kwota),
    promoActive: true,
  }
}

function tierStateFromPricing(pricing: PassTierPricing, fallbackRegular: string): Omit<HotpayKwotaState, 'loading' | 'error'> {
  const regularKwota = pricing.regularKwota?.trim() || fallbackRegular
  const promoActive =
    pricing.promoActive === true || isPromoKwota(pricing.kwota, regularKwota)
  return {
    kwota: pricing.kwota,
    regularKwota,
    label: formatKwotaPln(pricing.kwota),
    regularLabel: formatKwotaPln(regularKwota),
    amount: parseKwotaNumber(pricing.kwota),
    promoActive,
  }
}

export function useHotpayPricing(): HotpayPricingState {
  const [state, setState] = useState<HotpayPricingState>({
    pricing: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    void fetchHotpayPricing()
      .then((pricing) => {
        if (!cancelled) setState({ pricing, loading: false, error: null })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const message = e instanceof Error ? e.message : 'Nie udało się odczytać ceny.'
        setState({ pricing: null, loading: false, error: message })
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}

export function useHotpayKwota(tier: PassTier = 'full'): HotpayKwotaState {
  const { pricing, loading, error } = useHotpayPricing()
  const fallbackRegular = tier === 'full' ? FULL_REGULAR_KWOTA : MINI_REGULAR_KWOTA

  if (loading) {
    const fallback = fallbackTierState(tier)
    if (fallback) {
      return { ...fallback, loading: true, error: null }
    }
    return {
      kwota: null,
      regularKwota: fallbackRegular,
      label: null,
      regularLabel: formatKwotaPln(fallbackRegular),
      amount: null,
      promoActive: false,
      loading: true,
      error: null,
    }
  }

  if (error || !pricing) {
    const fallback = fallbackTierState(tier)
    if (fallback) {
      return { ...fallback, loading: false, error }
    }
    return {
      kwota: null,
      regularKwota: fallbackRegular,
      label: null,
      regularLabel: formatKwotaPln(fallbackRegular),
      amount: null,
      promoActive: false,
      loading: false,
      error,
    }
  }

  const tierPricing = pricingForTier(pricing, tier)
  return {
    ...tierStateFromPricing(tierPricing, fallbackRegular),
    loading: false,
    error: null,
  }
}

export { PLACES_COUNT_MINI, PLACES_COUNT_FULL }
